import { useSearchParams, useNavigate } from "react-router-dom";
import { useChapters } from "../hooks/useChapter";
import { useCallback, useState } from "react";
import { server2 } from "../constant/config";
import axios from "axios";
import { useAuthConfig } from "../hooks/useAuthConfig";
import Spinner from "../component/Spinner";

const AddPYQ = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const board = searchParams.get("boardId");
  const classId = searchParams.get("classId");
  const subject = searchParams.get("subjectId");
  const year = searchParams.get("year");
  const questionTypeFromURL = searchParams.get("questionType");

  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [chapterId, setChapterId] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState(
    questionTypeFromURL || ""
  );
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [questId, setQuestId] = useState(null);
  const [marks, setMarks] = useState(null);
  const [addingLoading, setAddingLoading] = useState(false);

  const axiosConfig = useAuthConfig();

  const { chapters, loading: chaptersLoading, error: chaptersError } =
    useChapters(board, classId, subject);

  // ---------- keep URL in sync ----------
  const updateSearchParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    navigate({ search: params.toString() }, { replace: true });
  };

  // ---------- Fetch Topics ----------
  const fetchTopics = useCallback(
    async (chapterId) => {
      if (!chapterId) return;

      try {
        setTopicsLoading(true);
        setError(null);

        const response = await axios.get(
          `${server2}/v1/topicsbychapter?chapterid=${chapterId}`,
          axiosConfig
        );

        const topicsData = response.data.content || [];

        const topicOptions = Array.isArray(topicsData)
          ? topicsData.map((t) => ({
              id: t?.id ?? t?.topicId ?? String(t),
              name: t?.name ?? String(t),
            }))
          : Object.entries(topicsData || {}).map(([id, obj]) => ({
              id,
              name: obj?.name ?? String(obj),
            }));

        setTopics(topicOptions);
      } catch (error) {
        console.error("Error fetching topics:", error);
        setError("Failed to fetch topics.");
        setTopics([]);
      } finally {
        setTopicsLoading(false);
      }
    },
    [axiosConfig]
  );

  // ---------- Add PYQ ----------
  const handleAddPYQ = async () => {
    if (
      !questId ||
      !marks ||
      !chapterId ||
      !selectedTopicId ||
      !selectedQuestionType
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    try {
      setAddingLoading(true);
      setError(null);

      await axios.post(
        `${server2}/v1/previousyearquestions`,
        {
          questionId: Number(questId),
          marks: Number(marks),
          topicId: Number(selectedTopicId),
          boardId: Number(board),
          classId: Number(classId),
          subjectId: Number(subject),
          chapterId: Number(chapterId),
          year: Number(year),
          typeOfQuestion: selectedQuestionType,
        },
        axiosConfig
      );

      setSuccess(true);
      setQuestId(null);
      setMarks(null);

      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error adding PYQ question:", error);
      setError(
        error.response?.data?.message ||
          "Failed to add PYQ question. Please try again later."
      );
    } finally {
      setAddingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="mx-auto max-w-2xl bg-white shadow-md rounded-lg p-6">
        <h1 className="text-2xl font-bold mb-6">Add Previous Year Question</h1>

        {/* Question Type */}
        <div className="mb-5">
          <label className="block mb-2 font-medium">Question Type</label>
          <select
            value={selectedQuestionType}
            onChange={(e) => {
              setSelectedQuestionType(e.target.value);
              updateSearchParam("questionType", e.target.value);
            }}
            className="w-full border p-2 rounded"
          >
            <option value="">Select question type...</option>
            <option value="MCQ">MCQ</option>
            <option value="SAQ">SAQ</option>
            <option value="LAQ">LAQ</option>
            <option value="TFQ">True/False</option>
            <option value="FITBQ">Fill in the blanks</option>
          </select>
        </div>

        {/* Chapter */}
        <div className="mb-5">
          <label className="block mb-2 font-medium">Select Chapter</label>
          {chaptersLoading ? (
            <Spinner />
          ) : (
            <select
              value={chapterId || ""}
              onChange={(e) => {
                setChapterId(e.target.value);
                setSelectedTopicId(null);
                fetchTopics(e.target.value);
              }}
              className="w-full border p-2 rounded"
            >
              <option value="">Select chapter...</option>
              {chapters?.map((c,index) => (
                <option key={c.id} value={c.id}>
                 {index + 1}. {c.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Topic */}
        <div className="mb-5">
          <label className="block mb-2 font-medium">Select Topic</label>
          {topicsLoading ? (
            <Spinner />
          ) : (
            <select
              value={selectedTopicId || ""}
              onChange={(e) => setSelectedTopicId(e.target.value)}
              disabled={!chapterId}
              className="w-full border p-2 rounded"
            >
              <option value="">Select topic...</option>
              {topics.map((t, index) => (
                <option key={t.id} value={t.id}>
                  {index + 1}. {t.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Question ID */}
        <input
          type="number"
          placeholder="Question ID"
          value={questId || ""}
          onChange={(e) => setQuestId(e.target.value)}
          className="w-full border p-2 rounded mb-4"
        />

        {/* Marks */}
        <input
          type="number"
          placeholder="Marks"
          value={marks || ""}
          onChange={(e) => setMarks(e.target.value)}
          onWheel={(e)=>e.target.blur()}
          className="w-full border p-2 rounded mb-6"
              />
              {chapterId && selectedTopicId && (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newWindow = window.open(
                                            `/addQuestion?topic=${selectedTopicId}&board=${board}&class=${classId}&subject=${subject}&chapter=${chapterId}&questionType=${selectedQuestionType}`,
                                            "_blank"
                                        );
                                        if (newWindow) newWindow.focus();
                                    }}
                                    disabled={addingLoading || topicsLoading}
                                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                >
                                    Add New Question
                                </button>
                            </div>
                        )}


        <button
          onClick={handleAddPYQ}
          disabled={
            !questId ||
            !marks ||
            !chapterId ||
            !selectedTopicId ||
            !selectedQuestionType ||
            addingLoading
          }
          className="w-full bg-indigo-600 text-white py-2 rounded disabled:opacity-50"
        >
          {addingLoading ? "Adding..." : "Add PYQ Question"}
        </button>

        {success && (
          <p className="text-green-600 mt-4">
            PYQ question added successfully!
          </p>
        )}
        {error && <p className="text-red-600 mt-4">{error}</p>}
      </div>
    </div>
  );
};

export default AddPYQ;
